import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import Field from "../components/Field";
import FormMessage from "../components/FormMessage";
import { ApiError, apiFetch } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch("/api/auth/password-reset/confirm", {
        method: "POST",
        body: { token, password },
      });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Something went wrong. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthShell eyebrow="Password" title="missing link.">
        <FormMessage tone="error">
          This page needs a reset token. Open the link from your email
          directly, or{" "}
          <Link to="/forgot-password" style={{ textDecoration: "underline" }}>
            request a new one
          </Link>
          .
        </FormMessage>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Password"
      title="new password."
      intro="Pick something at least 10 characters long. A passphrase works great."
    >
      {done ? (
        <FormMessage tone="success">
          Done. Taking you to the sign-in page…
        </FormMessage>
      ) : (
        <>
          {error && <FormMessage tone="error">{error}</FormMessage>}
          <form onSubmit={onSubmit} noValidate>
            <Field
              label="New password"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
            <Field
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
            />
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitting}
              style={{ width: "100%", marginTop: "0.5rem" }}
            >
              {submitting ? "Saving…" : "Set new password"}
            </button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
